import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader, Scan, ImagePlus, FileText, Sparkles, ChevronDown, Plane, X, RotateCcw, TrendingDown, Check } from 'lucide-react';
import { useDialog } from '../context/DialogContext';
import { useCapture } from '../context/CaptureContext';
import useExpenseForm from '../hooks/useExpenseForm';
import MobilePageHeader from '../components/mobile/MobilePageHeader';
import PreviewModal from '../components/PreviewModal';
import AutocompleteInput from '../components/AutocompleteInput';
import { getPreviewUrl } from '../utils/previewUrl';
import { CURRENCIES } from '../utils/currencies';
import { EXPENSE_CATEGORIES } from '../utils/categories';

function FilledBadge() {
  return (
    <span className="inline-flex items-center gap-0.5 ml-2 text-[11px] font-medium text-amber-700">
      <Sparkles className="w-3 h-3" />
      from receipt
    </span>
  );
}

function FieldLabel({ htmlFor, children, filled }) {
  return (
    <label htmlFor={htmlFor} className="flex items-center text-sm font-medium text-gray-700 mb-1">
      {children}
      {filled && <FilledBadge />}
    </label>
  );
}

const inputClass = 'block w-full rounded-xl border border-gray-300 px-3 py-3 text-base focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white';

/**
 * Mobile quick-confirm expense screen: receipt thumbnail, then amount,
 * date, category and trip; everything else behind "More details".
 */
function MobileExpenseForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useDialog();
  const { openScanner } = useCapture();
  const fileInputRef = useRef(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showMore, setShowMore] = useState(false);

  const {
    isEditing, loading, loadError, saving, file, preview, fileType,
    ocrLoading, ocrProgress, ocrFilled,
    formData, setField, handleChange, suggestions, trips, autoLinkedTrip,
    convertedAmount, conversionRate, processFile, clearFile, submit,
  } = useExpenseForm({ id, autoApplyOcr: true });

  useEffect(() => {
    if (loadError) {
      toast('Error loading expense', 'error');
      navigate('/expenses');
    }
  }, [loadError, navigate, toast]);

  // When editing, open "More details" if any of those fields has a value
  useEffect(() => {
    if (isEditing && !loading && (formData.merchant || formData.client || formData.description || formData.attendees)) {
      setShowMore(true);
    }
  }, [isEditing, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const goBack = () => {
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate('/expenses');
  };

  const startScan = () => openScanner((capturedFile, previewUrl) => processFile(capturedFile, previewUrl));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await submit();
    if (result.status === 'saved') {
      toast(isEditing ? 'Expense updated' : 'Expense saved');
      navigate('/expenses');
    } else if (result.status === 'queued') {
      toast('Saved offline — it will upload when you are back online', 'info');
      navigate('/expenses');
    } else {
      toast(result.message, 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const linkedTrip = trips.find(t => String(t.id) === formData.trip_id);
  const filled = (name) => ocrFilled.includes(name);

  return (
    <div className="pb-28">
      <MobilePageHeader
        title={isEditing ? 'Edit expense' : 'New expense'}
        onBack={goBack}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(e) => {
          const selected = e.target.files?.[0];
          e.target.value = '';
          if (selected) processFile(selected);
        }}
      />

      <form id="mobile-expense-form" onSubmit={handleSubmit} className="space-y-5">
        {/* Receipt */}
        {!preview ? (
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={startScan}
              className="flex flex-col items-center justify-center gap-2 h-32 rounded-2xl bg-blue-600 text-white font-medium active:bg-blue-700"
            >
              <Scan className="w-8 h-8" />
              Scan receipt
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex flex-col items-center justify-center gap-2 h-32 rounded-2xl border-2 border-dashed border-gray-300 bg-white text-gray-700 font-medium active:bg-gray-50"
            >
              <ImagePlus className="w-8 h-8 text-gray-500" />
              Photo or PDF
            </button>
          </div>
        ) : (
          <div className="flex gap-3 items-stretch">
            <button
              type="button"
              onClick={() => setShowPreview(true)}
              className="w-28 h-36 flex-shrink-0 rounded-xl bg-gray-100 overflow-hidden flex items-center justify-center"
              aria-label="View receipt"
            >
              {fileType === 'pdf' ? (
                <FileText className="w-12 h-12 text-red-500" />
              ) : (
                <img src={getPreviewUrl(preview)} alt="Receipt" className="w-full h-full object-cover" />
              )}
            </button>
            <div className="flex-1 min-w-0 flex flex-col justify-between py-1">
              <div>
                <p className="font-medium text-gray-900">{file ? 'Receipt attached' : 'Current receipt'}</p>
                {ocrLoading ? (
                  <div className="mt-2">
                    <p className="text-sm text-amber-700 flex items-center gap-1.5">
                      <Loader className="w-3.5 h-3.5 animate-spin" />
                      Reading receipt…
                    </p>
                    <div className="mt-1.5 h-1.5 bg-amber-100 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-500 rounded-full transition-all duration-200" style={{ width: `${ocrProgress}%` }} />
                    </div>
                  </div>
                ) : ocrFilled.length > 0 ? (
                  <p className="mt-1 text-sm text-amber-700 flex items-center gap-1">
                    <Sparkles className="w-3.5 h-3.5" />
                    Filled from the receipt — please check
                  </p>
                ) : null}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={startScan}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 text-sm text-gray-700 active:bg-gray-200"
                >
                  <RotateCcw className="w-4 h-4" />
                  Rescan
                </button>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 text-sm text-gray-700 active:bg-gray-200"
                >
                  <ImagePlus className="w-4 h-4" />
                  Replace
                </button>
                {file && !isEditing && (
                  <button
                    type="button"
                    onClick={clearFile}
                    className="p-2 rounded-lg bg-gray-100 text-gray-500 active:bg-gray-200"
                    aria-label="Remove receipt"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Amount + currency */}
        <div>
          <FieldLabel htmlFor="amount" filled={filled('amount')}>Amount</FieldLabel>
          <div className="flex rounded-xl border border-gray-300 bg-white focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 overflow-hidden">
            <input
              type="text"
              inputMode="decimal"
              id="amount"
              name="amount"
              autoComplete="off"
              placeholder="0.00"
              value={formData.amount}
              onChange={(e) => setField('amount', e.target.value.replace(',', '.'))}
              className="min-w-0 flex-1 px-4 py-3 text-3xl font-semibold text-gray-900 focus:outline-none"
            />
            <select
              name="currency"
              value={formData.currency}
              onChange={handleChange}
              className="w-24 flex-shrink-0 border-l border-gray-200 bg-gray-50 px-2 text-base font-medium focus:outline-none"
              aria-label="Currency"
            >
              {CURRENCIES.map((currency) => (
                <option key={currency.code} value={currency.code}>
                  {currency.flag} {currency.code}
                </option>
              ))}
            </select>
          </div>
          {formData.currency !== 'EUR' && formData.amount && parseFloat(formData.amount) > 0 && (
            <p className="mt-1.5 text-sm text-blue-700 flex items-center gap-1.5">
              <TrendingDown className="w-4 h-4" />
              {convertedAmount
                ? `≈ ${convertedAmount.toFixed(2)} EUR (1 ${formData.currency} = ${(conversionRate || 0).toFixed(4)})`
                : 'Converting…'}
            </p>
          )}
        </div>

        {/* Date */}
        <div>
          <FieldLabel htmlFor="date" filled={filled('date')}>Date</FieldLabel>
          <input
            type="date"
            id="date"
            name="date"
            required
            value={formData.date}
            onChange={handleChange}
            className={inputClass}
          />
        </div>

        {/* Category */}
        <div>
          <FieldLabel>Category</FieldLabel>
          <div className="flex flex-wrap gap-2">
            {EXPENSE_CATEGORIES.map(({ value, label }) => {
              const active = formData.category === value;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => setField('category', active ? '' : value)}
                  className={`inline-flex items-center gap-1 px-3.5 py-2 rounded-full text-sm font-medium border ${
                    active ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-300 text-gray-700'
                  }`}
                  aria-pressed={active}
                >
                  {active && <Check className="w-3.5 h-3.5" />}
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Trip */}
        <div>
          <FieldLabel htmlFor="trip_id">Trip</FieldLabel>
          {linkedTrip && autoLinkedTrip ? (
            <div className="flex items-center gap-2 px-3 py-3 rounded-xl border border-purple-200 bg-purple-50">
              <Plane className="w-5 h-5 text-purple-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-purple-900 truncate">{linkedTrip.name}</p>
                <p className="text-xs text-purple-700">Linked automatically from the date</p>
              </div>
              <button
                type="button"
                onClick={() => setField('trip_id', '')}
                className="p-2 -mr-1 rounded-full text-purple-700 active:bg-purple-100"
                aria-label="Remove trip"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <select
              id="trip_id"
              name="trip_id"
              value={formData.trip_id}
              onChange={handleChange}
              className={inputClass}
            >
              <option value="">No trip</option>
              {trips.map(trip => (
                <option key={trip.id} value={String(trip.id)}>{trip.name}</option>
              ))}
            </select>
          )}
        </div>

        {/* More details */}
        <div className="rounded-2xl bg-white border border-gray-200">
          <button
            type="button"
            onClick={() => setShowMore(v => !v)}
            className="w-full flex items-center justify-between px-4 py-3 text-left"
            aria-expanded={showMore}
          >
            <span className="font-medium text-gray-900">More details</span>
            <span className="flex items-center gap-2 text-sm text-gray-500">
              {!showMore && formData.merchant && <span className="truncate max-w-[10rem]">{formData.merchant}</span>}
              <ChevronDown className={`w-5 h-5 transition-transform ${showMore ? 'rotate-180' : ''}`} />
            </span>
          </button>
          {showMore && (
            <div className="px-4 pb-4 space-y-4">
              <div>
                <AutocompleteInput
                  id="merchant"
                  name="merchant"
                  label={filled('merchant') ? 'Merchant ✨' : 'Merchant'}
                  value={formData.merchant}
                  onChange={handleChange}
                  suggestions={suggestions.merchants}
                  placeholder="Merchant name"
                />
              </div>
              <div>
                <AutocompleteInput
                  id="client"
                  name="client"
                  label="Project/Client"
                  value={formData.client}
                  onChange={handleChange}
                  suggestions={suggestions.clients}
                  placeholder="Project or client identifier"
                />
              </div>
              <div>
                <FieldLabel htmlFor="description">Description</FieldLabel>
                <textarea
                  id="description"
                  name="description"
                  rows={2}
                  value={formData.description}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>
              <div>
                <AutocompleteInput
                  id="attendees"
                  name="attendees"
                  label="Attendees (comma-separated)"
                  value={formData.attendees}
                  onChange={handleChange}
                  suggestions={suggestions.attendees}
                  placeholder="John Doe, Jane Smith"
                  multiValue={true}
                />
              </div>
            </div>
          )}
        </div>
      </form>

      {/* Sticky save bar */}
      <div className="fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-gray-200 px-4 pt-3 pb-safe">
        <button
          type="submit"
          form="mobile-expense-form"
          disabled={saving}
          className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-xl bg-blue-600 text-white text-base font-semibold active:bg-blue-700 disabled:opacity-60"
        >
          {saving && <Loader className="w-5 h-5 animate-spin" />}
          {saving ? 'Saving…' : isEditing ? 'Save changes' : 'Save expense'}
        </button>
      </div>

      <PreviewModal
        file={file}
        fileUrl={getPreviewUrl(preview)}
        isPdf={fileType === 'pdf'}
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
      />
    </div>
  );
}

export default MobileExpenseForm;
